"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.default = SummaryInput;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_native_1 = require("react-native");
const react_redux_1 = require("react-redux");
const user_1 = require("../../store/selectors/user");
const document_1 = require("../../store/selectors/document");
const useDevice_1 = require("../../hooks/useDevice");
const useRequest_1 = __importDefault(require("../../hooks/useRequest"));
const useChatMessages_1 = require("../../hooks/useChatMessages");
const UploadedSources_1 = __importDefault(require("../Chat/UploadedSources"));
const ScrollCustomIndicator_1 = __importDefault(require("../ScrollCustomIndicator"));
const UploadMenu_1 = __importDefault(require("../Chat/UploadMenu"));
const SendButton_1 = __importDefault(require("./SendButton"));
const colors_1 = require("../../common/colors");
const spacing_1 = require("../../common/spacing");
const borders_1 = require("../../common/borders");
const constants_1 = require("../../common/constants");
const SOURCES_GAP = 8;
function SummaryInput({ fakeInput, onKeyPress, onChangeText, containerStyle, style, ...restProps }) {
    const { isMobileScreen } = (0, useDevice_1.useDevice)();
    const { request } = (0, useRequest_1.default)();
    const { sendMessageAsStream } = (0, useChatMessages_1.useChatMessages)();
    const selectedFileIds = (0, react_redux_1.useSelector)(document_1.selectSelectedFileIds);
    const isCharacterTyping = (0, react_redux_1.useSelector)(user_1.selectIsCharacterTyping);
    const isChatImageLoading = (0, react_redux_1.useSelector)(user_1.selectIsChatImageLoading);
    const selectedFontId = (0, react_redux_1.useSelector)((state) => state.fontSettings.selectedFontId); // Changed RootState to any
    const selectedFont = constants_1.AVAILABLE_FONTS.find(font => font.id === selectedFontId);
    const fontFamily = selectedFont?.fontFamily || 'system font';
    const [inputText, setInputText] = (0, react_1.useState)('');
    const [uploadedSources, setUploadedSources] = (0, react_1.useState)([]);
    const [sourcesHeight, setSourcesHeight] = (0, react_1.useState)(0);
    const inputRef = (0, react_1.useRef)(null);
    const isDisabled = (0, react_1.useMemo)(() => (!inputText.trim() || isCharacterTyping || isChatImageLoading), [inputText, isCharacterTyping, isChatImageLoading]);
    const wasUploadedImage = (0, react_1.useMemo)(() => (uploadedSources.some((source) => source.type === 'image')), [uploadedSources]);
    const wasUploadedFile = (0, react_1.useMemo)(() => (uploadedSources.some((source) => source.type === 'file')), [uploadedSources]);
    (0, react_1.useEffect)(() => {
        if (!uploadedSources.length) {
            setSourcesHeight(0);
        }
    }, [uploadedSources]);
    const adjustInputSize = (0, react_1.useCallback)(() => {
        const inputElement = inputRef.current;
        if (inputElement) {
            inputElement.style.height = "0";
            const newSize = (inputElement.offsetHeight - inputElement.clientHeight + inputElement.scrollHeight);
            inputElement.style.height = `${newSize}px`;
        }
    }, []);
    const sendNewMessage = (0, react_1.useCallback)(() => {
        const trimMessage = inputText.trim();
        if (trimMessage !== '') {
            setInputText('');
            setUploadedSources([]);
            request(sendMessageAsStream, {
                message: trimMessage,
                files: uploadedSources.map((source) => ({ file: source.file, url: source.url })),
            }, selectedFileIds);
        }
        setTimeout(() => {
            adjustInputSize();
        });
    }, [inputText, uploadedSources, selectedFileIds, sendMessageAsStream, adjustInputSize, request]);
    const handleSourcesLayout = (0, react_1.useCallback)((event) => {
        setSourcesHeight(event.nativeEvent.layout.height);
    }, []);
    const handlePress = (0, react_1.useCallback)(() => {
        inputRef.current?.blur();
        inputRef.current?.focus();
    }, []);
    const handleType = (0, react_1.useCallback)((event) => {
        const keyboardEvent = event.nativeEvent;
        if (keyboardEvent.key === 'Enter' && !keyboardEvent.shiftKey) {
            event.preventDefault();
            if (!isDisabled) {
                sendNewMessage();
            }
        }
        onKeyPress?.(event);
    }, [isDisabled, onKeyPress, sendNewMessage]);
    const handleTextChange = (0, react_1.useCallback)((text) => {
        setInputText(text);
        adjustInputSize();
        onChangeText?.(text);
    }, [adjustInputSize, onChangeText]);
    const handleUploadImages = (0, react_1.useCallback)((files) => {
        setUploadedSources((previousImages) => [
            ...previousImages,
            ...files.map((file) => ({ file, url: URL.createObjectURL(file), type: 'image' }))
        ]);
    }, []);
    const handleUploadFiles = (0, react_1.useCallback)((files) => {
        setUploadedSources((previousImages) => [
            ...previousImages,
            ...files.map((file) => ({
                file,
                url: URL.createObjectURL(file),
                name: file.name,
                type: 'file',
            }))
        ]);
    }, []);
    const handleRemoveSource = (0, react_1.useCallback)((url) => {
        setUploadedSources((previousImages) => previousImages.filter((previousImage) => (previousImage.url !== url)));
    }, []);
    return ((0, jsx_runtime_1.jsxs)(react_native_1.View, { style: [
            styles.container,
            isMobileScreen && styles.containerMobile,
            uploadedSources.length > 0 && styles.containerUpload,
            containerStyle,
        ], children: [(0, jsx_runtime_1.jsx)(UploadedSources_1.default, { sources: uploadedSources, onLayout: handleSourcesLayout, onCloseSource: handleRemoveSource }), (0, jsx_runtime_1.jsxs)(react_native_1.View, { style: styles.inputArea, children: [(0, jsx_runtime_1.jsx)(UploadMenu_1.default, { verticalOffset: sourcesHeight && (sourcesHeight + SOURCES_GAP), handleUploadImages: handleUploadImages, handleUploadFiles: handleUploadFiles, options: {
                            images: !wasUploadedFile,
                            files: !wasUploadedImage
                        } }), (0, jsx_runtime_1.jsx)(ScrollCustomIndicator_1.default, { customIndicator: !isMobileScreen, onPointerDown: handlePress, containerStyle: [styles.inputContainer, fakeInput && styles.fakeInputContainer], style: isMobileScreen && [
                            styles.inputContainer,
                            styles.inputContainerMobile,
                            fakeInput && styles.fakeInputContainer,
                        ], contentContainerStyle: styles.inputContentContainer, children: (0, jsx_runtime_1.jsx)(react_native_1.TextInput, { ref: inputRef, value: inputText, placeholder: 'Ask a question...', placeholderTextColor: 'white', onKeyPress: handleType, onChangeText: handleTextChange, style: [styles.input, style, { fontFamily: fontFamily }], multiline: true, ...restProps }) }), (0, jsx_runtime_1.jsx)(SendButton_1.default, { disabled: isDisabled, sendAction: sendNewMessage })] })] }));
}
;
const styles = react_native_1.StyleSheet.create({
    container: {
        backgroundColor: colors_1.Colors.DARK_600,
        gap: SOURCES_GAP,
        paddingVertical: 8,
        paddingHorizontal: 12,
        paddingTop: 8 - 1,
        borderTopWidth: 1,
        borderColor: colors_1.Colors.DARK_400,
        borderRadius: 40,
    },
    containerMobile: {
        paddingVertical: spacing_1.Spacing.SPACING_SM,
        paddingHorizontal: spacing_1.Spacing.SPACING_LG,
        paddingBottom: 10,
        borderRadius: 0,
    },
    containerUpload: {
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        borderBottomLeftRadius: 32,
        borderBottomRightRadius: 32,
    },
    inputArea: {
        gap: spacing_1.Spacing.SPACING_MD,
        flexDirection: 'row',
        alignItems: 'flex-end',
    },
    inputContainer: {
        backgroundColor: colors_1.Colors.NEUTRAL_100,
        maxHeight: 5 * 20 /* 5 Lines */ + 2 * 14 /* Padding */ + 2 * 1 /* Borders */,
        flex: 1,
        paddingVertical: 14 - 1,
        paddingHorizontal: 24 - 1,
        paddingRight: 14 - 1,
        borderWidth: 0.5,
        borderColor: colors_1.Colors.NEUTRAL_75,
        borderRadius: 38,
    },
    inputContainerMobile: {
        paddingVertical: 12,
        paddingLeft: spacing_1.Spacing.SPACING_LG,
        paddingRight: spacing_1.Spacing.SPACING_XS,
        borderRadius: borders_1.Borders.RADIUS_MD,
    },
    fakeInputContainer: {
        backgroundColor: colors_1.Colors.PRIMARY_700,
    },
    inputContentContainer: {
        gap: 10,
        flexDirection: 'row',
        alignItems: 'center',
    },
    input: {
        color: 'white',
        fontSize: 16,
        lineHeight: 20,
        height: 20,
        flex: 1,
        ...(react_native_1.Platform.OS === 'web' && {
            outlineStyle: 'none',
        })
    },
    iconContainer: {
        width: 32,
        height: 32,
        justifyContent: 'center',
        alignItems: 'center',
    },
});
